import React, { memo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Input } from "antd";
import { FieldWrapper } from "../FieldWrapper";
import { FieldComponentProps, TextAreaFieldConfig } from "models/formField";
import { getFieldError } from "helpers/formField.components.helper";
import styles from "./TextAreaField.module.scss";

const { TextArea } = Input;

/**
 * TextAreaField - Multi-line text input component integrated with React Hook Form
 */
export const TextAreaField: React.FC<FieldComponentProps<TextAreaFieldConfig>> =
  memo(({ config, control: controlProp, errors: errorsProp }) => {
    // Get form context if available
    const formContext = useFormContext();
    const control = controlProp ?? formContext?.control;
    const errors = errorsProp ?? formContext?.formState?.errors;

    const error = getFieldError(errors, config.name);

    if (!control) {
      console.error(
        `TextAreaField "${config.name}" requires control prop or FormProvider`,
      );
      return null;
    }

    return (
      <Controller
        name={config.name}
        control={control}
        defaultValue={config.defaultValue ?? ""}
        rules={config.validation}
        render={({ field }) => (
          <FieldWrapper
            label={config.label}
            name={config.name}
            error={error}
            tooltip={config.tooltip}
            required={!!config.validation?.required}
            className={config.className}
            hidden={config.hidden}
          >
            <TextArea
              {...field}
              id={config.name}
              placeholder={config.placeholder}
              disabled={config.disabled}
              readOnly={config.readOnly}
              rows={config.rows ?? 4}
              maxLength={config.maxLength}
              autoSize={config.autoSize}
              className={`${styles.textarea} ${error ? styles.textareaError : ""}`}
              aria-invalid={!!error}
              aria-describedby={error ? `${config.name}-error` : undefined}
              onChange={(e) => {
                field.onChange(e.target.value);
                config.onChange?.(e.target.value);
              }}
              onBlur={() => {
                field.onBlur();
                config.onBlur?.();
              }}
              {...config.data}
            />
          </FieldWrapper>
        )}
      />
    );
  });

TextAreaField.displayName = "TextAreaField";
